import React, { createContext, useContext, useEffect, useState } from 'react';

const WishlistContext = createContext(null);
const STORAGE_KEY = 'rl_wishlist';

export function WishlistProvider({ children }) {
  // Same hardening as CartContext: localStorage may hold entries written by
  // older app versions with a different shape — sanitize on hydration.
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const clean = parsed.filter((i) =>
        i && typeof i === 'object' &&
        i.product_id && typeof i.product_id === 'string' &&
        typeof i.name === 'string' &&
        typeof i.slug === 'string'
      ).map((i) => ({
        product_id: i.product_id,
        name: i.name,
        slug: i.slug,
        price: Number.isFinite(Number(i.price)) ? Number(i.price) : 0,
        compare_at_price: Number.isFinite(Number(i.compare_at_price)) ? Number(i.compare_at_price) : null,
        image: typeof i.image === 'string' ? i.image : '',
        category_slug: typeof i.category_slug === 'string' ? i.category_slug : '',
        added_at: Number.isFinite(Number(i.added_at)) ? Number(i.added_at) : Date.now(),
      }));
      if (clean.length !== parsed.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
      }
      return clean;
    } catch (e) {
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      return [];
    }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (e) { /* storage blocked */ }
  }, [items]);

  const has = (id) => items.some((i) => i.product_id === id);

  const toggle = (product) => {
    setItems((prev) => {
      const exists = prev.find((i) => i.product_id === product.id);
      if (exists) return prev.filter((i) => i.product_id !== product.id);
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          slug: product.slug,
          price: product.price,
          compare_at_price: product.compare_at_price,
          image: (product.images && product.images[0]) || '',
          category_slug: product.category_slug,
          added_at: Date.now(),
        },
      ];
    });
  };

  const remove = (id) => setItems((prev) => prev.filter((i) => i.product_id !== id));
  const clear = () => setItems([]);

  return (
    <WishlistContext.Provider value={{ items, has, toggle, remove, clear, count: items.length }}>
      {children}
    </WishlistContext.Provider>
  );
}

export const useWishlist = () => useContext(WishlistContext);
